import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { getUser } from 'redux/store/user/getters'
import { useSelector } from 'react-redux'
import { getLocalAccessToken } from 'helper/common'
import { BASE_SOCKET_TYPE, BASE_SOCKET_URL } from 'constants/socket'
import Socket from 'services/Socket'
import { emptyContextState } from './constants'
import { TWebSocketMessagePayload } from 'redux/store/chatSocket/types'
import {
  addUnreadSocketMessage,
  incrementTotalUnreadMessages
} from 'redux/store/chatSocket/slice'
import { useAppDispatch } from 'redux/createStore'
import { getUnreadMessagesActionAsync } from 'redux/store/chatSocket/actions'
import { updateChatStatusAndOrdering } from 'redux/store/chats/slice'
import { addNewMessage, updateReadByUsers } from 'redux/store/chatHistory/slice'
import { incrementUnreadMessages } from 'redux/store/chatDetails/slice'
import { parseSocketMessage } from './helpers'

export type TContextSocket = {
  localState: {
    socket: Socket | undefined
  }
}

const ChatSocketContext = createContext<TContextSocket>(emptyContextState)

const ChatSocketProvider: FC<PropsWithChildren> = ({ children }) => {
  const user = useSelector(getUser)
  const [socket, setSocket] = useState<TContextSocket['localState']['socket']>()
  const dispatch = useAppDispatch()

  const _createSocket = useCallback(() => {
    const token = getLocalAccessToken()
    if (token && !socket) {
      const socketUrl = new URL(BASE_SOCKET_URL)
      socketUrl.searchParams.append('token', token)
      setSocket(new Socket(socketUrl.href))
    }
  }, [socket])

  useEffect(() => {
    if (!user.uuid) {
      setSocket(undefined)
    } else if (!socket && user.uuid) {
      _createSocket()
      // initial trigger to get count of unread messages from BE
      dispatch(getUnreadMessagesActionAsync())
    }
  }, [_createSocket, dispatch, socket, user.uuid])

  useEffect(() => {
    if (socket) {
      socket.open()
      socket.onmessage = (message: MessageEvent) => {
        const data = JSON.parse(message.data) as TWebSocketMessagePayload
        dispatch(addUnreadSocketMessage(data))
        // update chat ordering based on new message received
        // if list exists - target chat will be applied in the top of the list locally on FE
        if (data.type === BASE_SOCKET_TYPE.NEW_MESSAGE_ADDED) {
          dispatch(incrementTotalUnreadMessages())
          dispatch(incrementUnreadMessages({ uuid: data.payload.chat_id }))
          // update order and chat info if it exists in the list
          dispatch(
            updateChatStatusAndOrdering({
              uuid: data.payload.chat_id,
              lastMessageSent: data.payload.message.created_at
            })
          )
          // add message to active chat history
          // inside it will check if current chat id is the same as the message chat id
          dispatch(
            addNewMessage({
              message: parseSocketMessage(data),
              chatId: data.payload.chat_id
            })
          )
        }
        if (data.type === BASE_SOCKET_TYPE.MESSAGE_READ) {
          dispatch(updateReadByUsers({ chatId: data.payload.chat_id }))
        }
      }
    }
    //it's possible to think about error catching on unplanned disconnection
    return () => {
      socket?.close()
    }
  }, [socket, dispatch])

  const context = useMemo(
    (): TContextSocket => ({
      localState: {
        socket
      }
    }),
    [socket]
  )

  return (
    <ChatSocketContext.Provider value={context}>
      {children}
    </ChatSocketContext.Provider>
  )
}

export const useChatSocket = () => useContext(ChatSocketContext)

export default ChatSocketProvider
